import { useEffect, useMemo, useRef, useState } from 'react';
import { throttle } from '@umu/umu-util';
import { $window, isElementPartiallyInViewport } from '@umu/umu-dom';

/**
- * 已弃用，请使用 useInViewport
- */
export const useElementIntoView = (element: HTMLElement | null, once = false, root?: HTMLElement): boolean => {
    const [isIntersecting, setIsIntersecting] = useState<boolean>(false);
    const observeRef = useRef<IntersectionObserver | null>(null);

    const scrollCallback = useMemo(() => {
        return throttle(() => {
            if (element) {
                const activeFlag = isElementPartiallyInViewport(element!, root);
                setIsIntersecting(activeFlag);
                if (once && activeFlag) {
                    removeScrollListener();
                }
            }
        }, 100);
    }, [element, root]);

    const removeScrollListener = () => {
        (root || $window).removeEventListener('scroll', scrollCallback);
    };

    const initObserverScheme = (el: HTMLElement) => {
        const options = {
            root: root || null,
            rootMargin: '0px',
            threshold: [0, 0.1]
        };
        const callback = (entries: IntersectionObserverEntry[]) => {
            const entrie = entries[0];
            if (entrie) {
                const activeFlag = entrie.intersectionRatio > 0;
                setIsIntersecting(activeFlag);
                if (once && activeFlag && observeRef.current) {
                    observeRef.current.unobserve(el);
                }
            }
        };

        const initObserver = observeRef.current || new IntersectionObserver(callback, options);
        initObserver.observe(el);
        observeRef.current = initObserver;
    };

    useEffect(() => {
        if (!element) {
            setIsIntersecting(false);
            return;
        }
        const activeFlag = isElementPartiallyInViewport(element, root);
        setIsIntersecting(activeFlag);
        if (once && activeFlag) {
            return;
        }

        if ('IntersectionObserver' in $window) {
            initObserverScheme(element);
        } else {
            (root || $window).addEventListener('scroll', scrollCallback);
        }

        return () => {
            if (observeRef.current) {
                observeRef.current.unobserve(element);
            } else {
                removeScrollListener();
            }
        };
    }, [element, root]);

    useEffect(() => {
        return () => {
            if (observeRef.current) {
                observeRef.current.disconnect();
                observeRef.current = null;
            } else {
                removeScrollListener();
            }
        };
    }, []);

    return isIntersecting;
};
